import React, { useMemo, useState } from 'react';
import './topCard.css';
import type { ITopData } from '../../models/ITopData';
import type { IFilter } from '../../models/IFilter.ts';
import { linkService } from '../../services/linkService';
import { DateFieldNames, filterService } from '../../services/filterService';
import { Skeleton } from '@mui/material';
type Props = {
  Data: ITopData[];
  Title: string;
  Footer: (setIsExpanded: (value: boolean) => void, isExpanded: boolean) => React.ReactNode;
  Filters: IFilter[];
  DateRangeMode: string;
  WorkLogLink: string;
  RecordsCount: number;
  isLink: boolean;
  FilterDataField?: string;
  openSelectedFilterHandler?: (employee: number | string) => void;
  isLoading?: boolean;
  showPrices?: boolean;
  currencySymbol?: string;
};

const TopCard = ({
  Data,
  Title,
  Footer,
  DateRangeMode,
  RecordsCount,
  Filters,
  WorkLogLink,
  FilterDataField,
  isLink,
  openSelectedFilterHandler,
  isLoading,
  showPrices = false,
  currencySymbol = '',
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayedData = useMemo(() => {
    if (RecordsCount !== -1 && !isExpanded) {
      return Data.slice(0, RecordsCount);
    }
    return Data;
  }, [Data, RecordsCount, isExpanded]);

  const openSelectedFilter = (employee: number | string) => {
    if (openSelectedFilterHandler) {
      return openSelectedFilterHandler(employee);
    }

    const dateFields = ['StartDate', 'EndDate'];
    const updatedFilters = Filters.filter((f) => dateFields.includes(f.DataField));
    updatedFilters.push(
      filterService.getEqualFilter(FilterDataField || DateFieldNames.Employee, employee.toString())
    );

    linkService.openWorkLogWithFilters(updatedFilters, WorkLogLink);
  };

  return (
    <div className="card">
      <h3>{Title}</h3>
      <div className="content">
        {!isLoading && displayedData.length > 0 ? (
          displayedData.map((rec, index) => (
            <div
              key={index}
              className={isLink ? 'item-link' : 'item'}
              onClick={
                isLink
                  ? () => {
                      if (FilterDataField === DateFieldNames.Project) {
                        openSelectedFilter(String(rec.Name));
                      } else {
                        openSelectedFilter(String(rec.Id));
                      }
                    }
                  : undefined
              }
            >
              <span>{rec.Name}</span>
              <span>
                {showPrices
                  ? `${currencySymbol}${(Number.isFinite(rec.TotalPrice) ? rec.TotalPrice : 0).toFixed(2)}`
                  : '-'}
              </span>
            </div>
          ))
        ) : isLoading ? (
          <div>
            <Skeleton variant="text" width={90} />
            <Skeleton variant="text" />
          </div>
        ) : (
          <p className="no-data">There is no data for the selected {DateRangeMode}</p>
        )}
      </div>
      {!isLoading && Data && Data?.length > RecordsCount && Footer(setIsExpanded, isExpanded)}
    </div>
  );
};

export default TopCard;
